<!--
  Prepare: what to do before (#/prepare; was "Your plan" at #/plan, which redirects here since
  v0.3.0, DESIGN-DELTA-v3 §1): the actions to take before anything happens. Free steps, what to buy
  and when, decisions and savings, by month: this month first and free steps first. Each item can
  be checked off with what was paid. Progress per consequence, guardrail warnings (never blocks),
  money being saved toward bigger items, and the whole schedule. Steps count only what the
  household does from here: what it already had (its own list, and the everyday basics the plan
  assumes) is shown apart as "Already have".

  "Print your preparation plan" prints the engine's Prepare sheet (`prepare_markdown`) as a clean
  sheet (PrepareSheet: title, date, the site's Markdown renderer, sources in two columns), in a
  region that exists only on paper; the browser's own Print on this tab prints the same sheet.

  Months are numbered exactly as the printed packet numbers them (the packet is the oracle): month 0
  begins on the plan date and reads "This month (October 2026)", the next is "Month 1 (November
  2026)", so a purchase has the same month on the screen as in the packet's checklists.

  Contract v2: in bare-minimum mode (`Plan.minimum_kit`) a banner says the smallest three-day kit
  comes first and lists what falls beyond three years (the `plan_too_long` warning's `related`
  items); decisions show as decisions, grouped the way the packet groups them (one "Decide this
  month" block with the decisions named on one line, each still ticked off on its own card, and
  one line in the month lists), so a month with many decisions stays short; the long-horizon
  section (`Plan.long_horizon`) follows the whole plan.
-->
<script lang="ts">
  import { tick } from 'svelte';
  import BucketGauge from '../components/BucketGauge.svelte';
  import ConfirmDialog from '../components/ConfirmDialog.svelte';
  import PrepareSheet from '../components/PrepareSheet.svelte';
  import Icon from '../components/Icon.svelte';
  import ItemCard from '../components/ItemCard.svelte';
  import PlanGate from '../components/PlanGate.svelte';
  import SavingsTrack from '../components/SavingsTrack.svelte';
  import Sources from '../components/Sources.svelte';
  import Warning from '../components/Warning.svelte';
  import type { PlanItem, PlanOutput } from '../engine/types';
  import { useApp } from '../lib/app.svelte';
  import { addMonths, formatDate, formatMonth, monthsBetween, planMonthLabel, planMonthPhrase, quantity, usd } from '../lib/format';
  import { CONFIDENCE_QUESTION, CONFIDENCE_SCALE, stageLine } from '../lib/labels';
  import { allPlanItems, bucketName, catalogueItem, decisionList, itemSourceIds, keyedItems, tierName } from '../lib/lookup';
  import { href } from '../lib/router.svelte';

  const app = useApp();
  let confirmRestart = $state(false);
  /** The Prepare sheet is on the page (for paper only) while it is being printed. */
  let printing = $state(false);

  async function printSheet() {
    printing = true;
    await tick();
    window.print();
  }

  // The browser's own Print on this tab prints the sheet too, and the page forgets it afterwards.
  $effect(() => {
    const before = () => (printing = true);
    const after = () => (printing = false);
    window.addEventListener('beforeprint', before);
    window.addEventListener('afterprint', after);
    return () => {
      window.removeEventListener('beforeprint', before);
      window.removeEventListener('afterprint', after);
    };
  });

  const planningDate = $derived(app.plan?.input.planning_date ?? app.today());
  const currentMonth = $derived(Math.max(0, monthsBetween(planningDate, app.today())));

  const GROUPS: { name: string; categories: string[] }[] = [
    { name: 'Safety at home', categories: ['fire', 'home', 'security'] },
    { name: 'Plans and papers', categories: ['plan', 'documents', 'comms', 'money'] },
    { name: 'Water, food and medicine', categories: ['water', 'food', 'medical', 'thermal', 'animals'] },
    { name: 'Getting around', categories: ['transport'] },
    { name: 'Neighbors', categories: ['community'] },
  ];

  function groupFree(items: PlanItem[]): { name: string; items: PlanItem[] }[] {
    const out = GROUPS.map((g) => ({
      name: g.name,
      items: items.filter((i) => g.categories.includes(catalogueItem(app.catalogue, i.item_id)?.category ?? '')),
    }));
    const placed = new Set(out.flatMap((g) => g.items));
    const rest = items.filter((i) => !placed.has(i));
    if (rest.length) out.push({ name: 'Other', items: rest });
    return out.filter((g) => g.items.length);
  }

  /** "This month (October 2026)", "Month 4 (February 2027)": the packet's numbering (month 0 is the plan date's month). */
  function monthLabel(index: number): string {
    return planMonthLabel(planningDate, index, currentMonth);
  }

  function spend(items: PlanItem[]): number {
    return items.filter((i) => !i.done).reduce((s, i) => s + i.est_cost_usd, 0);
  }

  /** Done without being checked off here: on the "What you already have" list, or an assumed everyday basic. */
  function alreadyHad(item: PlanItem): boolean {
    return !!item.done && !app.purchaseFor(item.item_id, item.tier);
  }

  /** Steps done and to do, leaving out what the household already had. */
  function counts(output: PlanOutput) {
    const all = allPlanItems(output).map((x) => x.item);
    const had = all.filter(alreadyHad).length;
    return { done: all.filter((i) => i.done).length - had, had, total: all.length - had };
  }

  /** Money (cash in small bills) is set aside, not bought. */
  function isMoney(itemId: string): boolean {
    return catalogueItem(app.catalogue, itemId)?.unit === 'dollar';
  }

  /** Sources behind one item's quantity and price, for the short lists. */
  function sourcesOf(output: PlanOutput, item: PlanItem): string[] {
    return item.kind === 'free_action' ? [] : itemSourceIds(app.catalogue, output, item.item_id);
  }

  function restart() {
    if (app.plan) app.plan.input.planning_date = app.today();
  }

  /** What falls beyond three years in bare-minimum mode: the items the `plan_too_long` warning names. */
  function deferredNames(output: PlanOutput): string[] {
    const related = output.warnings.find((w) => w.id === 'plan_too_long')?.related ?? [];
    return related.map((id) => catalogueItem(app.catalogue, id)?.name).filter((n): n is string => !!n);
  }

  /** Any duration target of a month or more (the long-horizon section then appears by itself). */
  function hasLongTarget(output: PlanOutput): boolean {
    return output.buckets.some((b) => b.target.kind === 'days' && b.target.value >= 30);
  }
</script>

<!--
  Decisions (insurance, ID papers, home repairs) in one block, as the packet's "Decide this month"
  line: named on one line, each still on its own card to tick off, folded so a month with many
  decisions stays short.
-->
{#snippet decide(decisions: PlanItem[], heading: string, headingId: string)}
  <h3 id={headingId}>{heading} ({decisions.length})</h3>
  <p class="section-intro">
    Choices about insurance, papers and your home. They cost nothing from your supplies budget: {decisionList(decisions)}.
  </p>
  <details class="decisions">
    <summary>See each one, and tick it off when you have decided</summary>
    {#each keyedItems(decisions) as { key, item } (key)}<ItemCard {item} compact />{/each}
  </details>
{/snippet}

<div class="page prepare-page" class:printing>
  <h1 id="page-title" tabindex="-1">Prepare: what to do before</h1>
  <PlanGate>
    {#snippet children(output)}
      {@const months = output.plan.months}
      {@const earlier = months.filter((m) => m.index < currentMonth).flatMap((m) => m.items.filter((i) => !i.done))}
      {@const thisMonth = months.find((m) => m.index === currentMonth)}
      {@const thisItems = (thisMonth?.items ?? []).filter((i) => !i.done)}
      {@const nextMonth = months.find((m) => m.index > currentMonth)}
      {@const done = allPlanItems(output).filter((x) => x.item.done && !alreadyHad(x.item)).map((x) => x.item)}
      {@const had = allPlanItems(output).filter((x) => alreadyHad(x.item)).map((x) => x.item)}
      {@const c = counts(output)}
      {@const monthly = app.plan?.input.finances.monthly_budget_usd ?? 0}
      <p class="lead">
        {stageLine(app.plan?.input.stage)} The things to do before anything happens: free steps, what to buy and when, decisions to make,
        and money to set aside. Month by month, free steps come first, then the cheapest protection for your risks, within your budget.
      </p>
      <p class="button-row print-row no-print">
        <button type="button" class="button" onclick={printSheet}><Icon name="print" /> Print your preparation plan</button>
        <span class="small muted">The month-by-month steps, decisions and upkeep on paper, to keep with your binder.</span>
      </p>

      <ul class="stats" aria-label="Summary">
        <li class="card">
          <span class="stat__label">This month</span>
          <span class="stat__value">
            {#if (thisMonth?.budget_usd ?? 0) > 0}
              {usd(thisMonth?.budget_usd ?? 0)} to spend
            {:else if monthly > 0}
              Free steps first
            {:else}
              Free steps only
            {/if}
          </span>
          {#if (thisMonth?.budget_usd ?? 0) <= 0 && monthly > 0}<span class="small muted">{usd(monthly)} a month from next month</span>{/if}
        </li>
        <li class="card">
          <span class="stat__label">Done so far</span>
          <span class="stat__value">{c.done} of {c.total} {c.total === 1 ? 'step' : 'steps'}</span>
          {#if c.had > 0}<span class="small muted">Already have: {c.had}</span>{/if}
        </li>
        <li class="card">
          <span class="stat__label">Every target covered</span>
          <span class="stat__value">
            {#if output.plan.done_month !== undefined}
              by {formatMonth(addMonths(planningDate, output.plan.done_month))}
            {:else if monthly <= 0 && (app.plan?.input.finances.one_off_budget_usd ?? 0) <= 0}
              once you add a budget
            {:else}
              in more than three years
            {/if}
          </span>
        </li>
      </ul>

      {#if output.plan.minimum_kit}
        {@const deferred = deferredNames(output)}
        <section class="minimum card" aria-labelledby="minimum-title">
          <h2 id="minimum-title">Bare minimum first</h2>
          <p>
            {app.plan?.input.dials.minimum_kit ? 'You asked for the bare minimum first.' : 'At your budget the full plan would take more than three years, so it starts with the bare minimum.'}
            The first things to get are the smallest kit that covers three days of water, light, warmth and medicine. Everything else comes after
            it, in the usual order.
          </p>
          {#if deferred.length}
            <details class="deferred">
              <summary>Beyond three years at this budget: {deferred.length} {deferred.length === 1 ? 'step' : 'steps'}</summary>
              <ul>
                {#each deferred as name, i (i)}<li>{name}</li>{/each}
              </ul>
              <p class="small muted">A little more each month, or a less cautious setting on the risks screen, brings them closer.</p>
            </details>
          {/if}
        </section>
      {/if}

      {@const shownWarnings = output.plan.minimum_kit ? output.warnings.filter((w) => w.id !== 'plan_too_long') : output.warnings}
      {#if shownWarnings.length}
        <section aria-label="Things to look at">
          {#each shownWarnings as w (w.id)}<Warning warning={w} />{/each}
        </section>
      {/if}

      {#if earlier.length}
        {@const earlierDecisions = earlier.filter((i) => i.decision)}
        <section aria-labelledby="earlier-title">
          <h2 id="earlier-title">Still to do from earlier months</h2>
          <p class="section-intro">Plans slip; that is normal. These still help, in this order.</p>
          {#each keyedItems(earlier.filter((i) => !i.decision)) as { key, item } (key)}<ItemCard {item} />{/each}
          {#if earlierDecisions.length}{@render decide(earlierDecisions, 'Still to decide', 'earlier-decide-title')}{/if}
        </section>
      {/if}

      <section aria-labelledby="this-title">
        <h2 id="this-title">{monthLabel(currentMonth)} <span class="muted h-note">from {formatDate(addMonths(planningDate, currentMonth))}</span></h2>
        {#if thisItems.length === 0}
          <p class="card">Nothing new to do this month{output.plan.envelopes.length ? '; your budget is saving toward a bigger item below' : ''}. {nextMonth ? 'The next step is shown below.' : ''}</p>
        {:else}
          {@const decisions = thisItems.filter((i) => i.decision)}
          {@const free = thisItems.filter((i) => i.kind === 'free_action' && !i.decision)}
          {@const buy = thisItems.filter((i) => i.kind !== 'free_action' && !i.decision)}
          {#if free.length}
            <h3>Free steps ({free.length})</h3>
            <p class="section-intro">These cost nothing and cover more than you might expect. Do them in any order.</p>
            {#each groupFree(free) as group (group.name)}
              <h4 class="group">{group.name}</h4>
              {#each keyedItems(group.items) as { key, item } (key)}<ItemCard {item} compact />{/each}
            {/each}
          {/if}
          {#if decisions.length}{@render decide(decisions, 'Decide this month', 'decide-title')}{/if}
          {#if buy.length}
            <h3>To get <span class="muted h-note">about {usd(spend(buy))} of {usd(thisMonth?.budget_usd ?? 0)}</span></h3>
            {#each keyedItems(buy) as { key, item } (key)}<ItemCard {item} />{/each}
          {/if}
        {/if}
      </section>

      {#if nextMonth && nextMonth.index !== currentMonth}
        {@const nextOpen = nextMonth.items.filter((i) => !i.done)}
        {@const nextDecisions = nextOpen.filter((i) => i.decision)}
        <section aria-labelledby="next-title">
          <h2 id="next-title">{monthLabel(nextMonth.index)} <span class="muted h-note">from {formatDate(addMonths(planningDate, nextMonth.index))}</span></h2>
          <ul class="preview">
            {#each keyedItems(nextOpen.filter((i) => !i.decision)) as { key, item } (key)}
              <li>
                {item.name}: {item.kind === 'free_action' ? 'free' : `${quantity(item.quantity, item.unit)}, about ${usd(item.est_cost_usd)}`}
                <Sources ids={sourcesOf(output, item)} variant="inline" what={item.name} />
              </li>
            {/each}
            {#if nextDecisions.length}
              <li class="decide-line"><strong>Decide</strong> ({nextDecisions.length}): {decisionList(nextDecisions)}. <span class="muted">No cost to your supplies budget.</span></li>
            {/if}
          </ul>
        </section>
      {/if}

      <section aria-labelledby="progress-title">
        <h2 id="progress-title">Where you are, and where your plan takes you</h2>
        <p class="section-intro">
          The solid bar is what you have now: what you already own and the steps you have checked off. The striped bar is how much of each
          target the plan covers once every step in it is done.
        </p>
        <div class="progress card">
          {#each output.buckets.filter((b) => b.target.kind === 'days') as b (b.id)}<BucketGauge bucket={b} compact />{/each}
        </div>
        <p class="small muted">
          Your risks point to <strong>{tierName(app.catalogue, output.tier_recommended).toLowerCase()}</strong> of supplies.
          {#if output.tier_reached !== 'now'}You have reached <strong>{tierName(app.catalogue, output.tier_reached).toLowerCase()}</strong>.{/if}
        </p>
      </section>

      {#if output.plan.envelopes.length}
        <section aria-labelledby="saving-title">
          <h2 id="saving-title">Saving up for</h2>
          <p class="section-intro">These cost more than a month's budget, so the plan sets money aside for them over a few months.</p>
          <ul class="envelopes">
            {#each output.plan.envelopes as e, i (i)}
              {@const month = months.find((m) => m.items.some((x) => x.item_id === e.item_id && !x.done && Math.abs(x.est_cost_usd - e.needed_usd) < 0.01))}
              <li class="card">
                <strong>{catalogueItem(app.catalogue, e.item_id)?.name ?? e.item_id}</strong>: about {usd(e.needed_usd)}{#if month}; {isMoney(e.item_id)
                    ? 'all set aside'
                    : 'ready to buy'}
                  in {planMonthPhrase(planningDate, month.index)}{/if}.
                <Sources ids={itemSourceIds(app.catalogue, output, e.item_id)} variant="inline" what={catalogueItem(app.catalogue, e.item_id)?.name ?? e.item_id} />
              </li>
            {/each}
          </ul>
        </section>
      {/if}

      <section aria-labelledby="all-title">
        <h2 id="all-title">The whole plan</h2>
        {#each months as m (m.index)}
          {@const open = m.items.filter((i) => !i.done)}
          {#if open.length}
            {@const monthDecisions = open.filter((i) => i.decision)}
            <details class="month">
              <summary>
                <span>{monthLabel(m.index)}</span>
                <span class="muted small">{open.length} {open.length === 1 ? 'step' : 'steps'}{spend(open) > 0 ? `, about ${usd(spend(open))}` : ''}</span>
              </summary>
              <ul class="month__list">
                {#each keyedItems(open.filter((i) => !i.decision)) as { key, item } (key)}
                  <li>
                    <span>{item.name}</span>
                    <span class="muted">{item.kind === 'free_action' ? 'free' : `${quantity(item.quantity, item.unit)}, about ${usd(item.est_cost_usd)}`}</span>
                    <span class="chip">{bucketName(app.catalogue, item.buckets[0] ?? '')}</span>
                    <Sources ids={sourcesOf(output, item)} variant="inline" what={item.name} />
                  </li>
                {/each}
                {#if monthDecisions.length}
                  <li class="decide-line">
                    <span><strong>Decide</strong> ({monthDecisions.length}): {decisionList(monthDecisions)}</span>
                    <span class="muted">free</span>
                    <span class="chip">Decisions</span>
                  </li>
                {/if}
              </ul>
            </details>
          {/if}
        {/each}
        {#if output.plan.done_month !== undefined}
          <p class="done-note">
            <Icon name="check" /> After {planMonthPhrase(planningDate, output.plan.done_month)} you are done for your risk. From then on, keeping it up takes a few minutes a month.
            <a href={href('maintain')}>See what to keep up</a>
          </p>
        {/if}
      </section>

      {#if output.plan.long_horizon?.length}
        {@const having = output.plan.long_horizon.filter((i) => i.kind !== 'free_action')}
        {@const learning = output.plan.long_horizon.filter((i) => i.kind === 'free_action')}
        <section aria-labelledby="long-title">
          <h2 id="long-title">If it lasts for months</h2>
          <p class="section-intro">
            {hasLongTarget(output) ? 'One of your targets is a month or more.' : 'You asked to see this part of the plan.'} Nobody can stockpile for
            months, and you do not need to. These keep working when an outage lasts: a few things worth having, and pointers to learn from.
            They are not part of the monthly schedule.
          </p>
          {#if having.length}
            <h3>Worth having</h3>
            {#each keyedItems(having) as { key, item } (key)}<ItemCard {item} />{/each}
          {/if}
          {#if learning.length}
            <h3>Worth learning</h3>
            {#each keyedItems(learning) as { key, item } (key)}<ItemCard {item} compact />{/each}
          {/if}
        </section>
      {/if}

      {#if done.length}
        <section aria-labelledby="done-title">
          <h2 id="done-title">Done <span class="muted h-note">{done.length}</span></h2>
          <details>
            <summary>Show what you've done</summary>
            {#each keyedItems(done) as { key, item } (key)}<ItemCard {item} />{/each}
          </details>
        </section>
      {/if}

      {#if had.length}
        <section aria-labelledby="had-title">
          <h2 id="had-title">Already have <span class="muted h-note">{had.length}</span></h2>
          <p class="section-intro">
            What the plan counts as already in your home: what you listed, and the everyday basics most homes have. Change them on
            <a href={href('have')}>What you already have</a>.
          </p>
          <details>
            <summary>Show what you already have</summary>
            {#each keyedItems(had) as { key, item } (key)}<ItemCard {item} />{/each}
          </details>
        </section>
      {/if}

      <SavingsTrack
        income={output.buckets.find((b) => b.id === 'income')}
        track={output.plan.savings_track}
        homeLoss={undefined}
        homeItems={[]}
        {planningDate}
        doneMonth={output.plan.done_month}
        firstMilestone={output.plan.first_milestone}
      />

      {#if app.plan}
        <section class="confidence card" aria-labelledby="conf-title">
          <h2 id="conf-title">How sure do you feel now?</h2>
          <fieldset>
            <legend>{CONFIDENCE_QUESTION}</legend>
            {#if app.plan.confidence.before !== undefined}
              <p class="help">When you started you said {app.plan.confidence.before} out of 5.</p>
            {/if}
            <div class="scale">
              {#each [1, 2, 3, 4, 5] as n (n)}
                <label class="choice">
                  <input
                    type="radio"
                    name="confidence-after"
                    checked={app.plan.confidence.after === n}
                    onchange={() => {
                      if (app.plan) app.plan.confidence.after = n;
                    }}
                  />
                  <span class="choice__text"><strong>{n}</strong><span class="choice__help">{CONFIDENCE_SCALE[n]}</span></span>
                </label>
              {/each}
            </div>
          </fieldset>
          {#if app.plan.confidence.after !== undefined && app.plan.confidence.before !== undefined && app.plan.confidence.after > app.plan.confidence.before}
            <p class="good"><Icon name="check" /> Up from {app.plan.confidence.before}. Each step you take counts.</p>
          {/if}
        </section>
      {/if}

      {#if app.prefs.expert}
        <section aria-labelledby="req-title">
          <h2 id="req-title">What the targets turn into (expert view)</h2>
          <!-- Wide tables scroll sideways on phones; a focusable, labelled region lets keyboard users scroll it. -->
          <!-- svelte-ignore a11y_no_noninteractive_tabindex -->
          <div class="table-wrap" tabindex="0" role="region" aria-label="What the targets turn into">
            <table>
              <thead><tr><th scope="col">For</th><th scope="col">Amount</th><th scope="col">How it was worked out</th><th scope="col">Rule</th><th scope="col">Sources</th></tr></thead>
              <tbody>
                {#each output.requirements as r (r.id)}
                  <tr>
                    <td>{bucketName(app.catalogue, r.bucket)}</td>
                    <td class="num">{quantity(r.quantity, r.unit)}</td>
                    <td>{r.plain}</td>
                    <td><code>{r.rule}</code></td>
                    <td><Sources ids={r.citations} variant="inline" what={quantity(r.quantity, r.unit)} /></td>
                  </tr>
                {/each}
              </tbody>
            </table>
          </div>
        </section>
      {/if}

      <section class="schedule small muted" aria-label="Schedule">
        <p>
          Your plan started on {formatDate(planningDate)}.
          <button type="button" class="button button--quiet button--small" onclick={() => (confirmRestart = true)}>Restart the schedule from today</button>
        </p>
      </section>
    {/snippet}
  </PlanGate>
  <!-- Paper only: on the page while printing (the button above, or the browser's own Print). -->
  <div class="print-only prepare-print">
    {#if printing && app.result.output}
      <PrepareSheet markdown={app.result.output.prepare_markdown} planDate={planningDate} appVersion={__RR_APP_VERSION__} />
    {/if}
  </div>
</div>

<ConfirmDialog bind:open={confirmRestart} title="Restart the schedule from today?" confirmLabel="Restart from today" cancelLabel="Keep the schedule" onconfirm={restart}>
  <p>This month becomes month 0 of your plan again. Nothing you have checked off is lost.</p>
  <p>If you printed your preparation plan, print it again so its months match the screen.</p>
  <p>If you already spent the one-off amount, set it to $0 on the Money screen so it isn't counted again.</p>
</ConfirmDialog>

<style>
  .print-row {
    margin: 0 0 var(--s4);
    align-items: center;
  }
  @media print {
    /* Printing the sheet: the sheet alone, never the screen around it. */
    .prepare-page.printing > :global(:not(.prepare-print)) {
      display: none !important;
    }
    .prepare-page:not(.printing) > .prepare-print {
      display: none !important;
    }
  }
  .stats {
    list-style: none;
    padding: 0;
    margin: var(--s4) 0 var(--s5);
    display: grid;
    gap: var(--s3);
    grid-template-columns: repeat(auto-fit, minmax(min(100%, 14rem), 1fr));
  }
  .stats li + li {
    margin-top: 0;
  }
  .stats .card {
    display: flex;
    flex-direction: column;
    gap: var(--s1);
    padding: var(--s3) var(--s4);
  }
  .stat__label {
    font-size: var(--text-sm);
    color: var(--text-muted);
    font-weight: 600;
  }
  .stat__value {
    font-size: var(--text-lg);
    font-weight: 700;
  }
  .h-note {
    font-size: var(--text-base);
    font-weight: 500;
  }
  .group {
    margin: var(--s4) 0 0;
    font-size: var(--text-sm);
    text-transform: uppercase;
    letter-spacing: 0.04em;
    color: var(--text-muted);
  }
  .preview li + li {
    margin-top: var(--s1);
  }
  .progress {
    padding-top: var(--s2);
    padding-bottom: var(--s2);
  }
  .envelopes {
    list-style: none;
    padding: 0;
    display: grid;
    gap: var(--s2);
  }
  .envelopes li + li {
    margin-top: 0;
  }
  .month summary {
    display: flex;
    flex-wrap: wrap;
    gap: var(--s1) var(--s3);
    border-bottom: 1px solid var(--border);
  }
  .month__list {
    list-style: none;
    padding: var(--s2) 0 var(--s3);
    margin: 0;
  }
  .month__list li {
    display: flex;
    flex-wrap: wrap;
    gap: var(--s1) var(--s3);
    align-items: center;
    padding: var(--s1) 0;
  }
  .done-note,
  .good {
    display: flex;
    gap: var(--s2);
    align-items: flex-start;
    color: var(--good);
    font-weight: 600;
  }
  .done-note a {
    font-weight: 600;
  }
  .confidence {
    margin-top: var(--s6);
  }
  .confidence h2 {
    margin-top: 0;
  }
  .scale {
    display: grid;
    gap: var(--s2);
    grid-template-columns: repeat(auto-fit, minmax(7rem, 1fr));
  }
  .schedule {
    margin-top: var(--s6);
  }
  .minimum {
    margin: 0 0 var(--s5);
    border-left: 6px solid var(--accent);
  }
  .minimum h2 {
    margin-top: 0;
    font-size: var(--text-lg);
  }
  .deferred summary,
  .decisions summary {
    color: var(--accent);
    min-height: 36px;
    font-weight: 600;
  }
  .decisions {
    margin-bottom: var(--s4);
  }
</style>
